import type { RoundStatus, SupplierRoundSummary } from "@dishour/shared";
import { useState } from "react";
import { useAdminRounds, useCreateRound, useSkus, useUpdateRoundStatus } from "../../api/admin.js";
import { AdminShell } from "../../components/AdminShell.js";
import { DataTable, type Column } from "../../components/DataTable.js";
import { Field, buttonClass, inputClass } from "../../components/Field.js";
import { ProgressBar } from "../../components/ProgressBar.js";
import { CommitmentStatusBadge, RoundStatusBadge } from "../../components/StatusBadge.js";
import { formatDateTime, formatMoney, formatQty } from "../../lib/format.js";

const NEXT_ACTIONS: Partial<Record<RoundStatus, { to: RoundStatus; label: string }[]>> = {
  open: [
    { to: "confirming", label: "마감하고 최종 확인 열기" },
    { to: "cancelled", label: "취소" },
  ],
  confirming: [
    { to: "confirmed", label: "확정 (미확인 참여는 no-show 처리)" },
    { to: "cancelled", label: "취소" },
  ],
  confirmed: [{ to: "closed", label: "종료" }],
  cancelled: [{ to: "closed", label: "종료" }],
};

function RoundDetail({ round }: { round: SupplierRoundSummary }) {
  const updateStatus = useUpdateRoundStatus();

  return (
    <div className="rounded-xl border border-brand-200 bg-brand-50 p-4">
      <ul className="mb-3 text-sm">
        {round.participants.map((p) => (
          <li key={p.restaurantId} className="flex items-center justify-between border-t border-brand-100 py-1 first:border-0">
            <span>{p.restaurantName}</span>
            <span className="flex items-center gap-2 text-stone-600">
              {formatQty(p.qty, round.sku)}
              <CommitmentStatusBadge status={p.status} />
            </span>
          </li>
        ))}
        {round.participants.length === 0 ? <li className="py-1 text-stone-500">아직 참여한 식당이 없습니다.</li> : null}
      </ul>
      <div className="flex flex-wrap gap-2">
        {(NEXT_ACTIONS[round.status] ?? []).map((action) => (
          <button
            key={action.to}
            type="button"
            className="rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm disabled:opacity-50"
            disabled={updateStatus.isPending}
            onClick={() => updateStatus.mutate({ id: round.id, status: action.to })}
          >
            {action.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export function AdminRoundsPage() {
  const rounds = useAdminRounds();
  const skus = useSkus();
  const createRound = useCreateRound();
  const [form, setForm] = useState({
    skuId: "",
    opensAt: "",
    closesAt: "",
    thresholdQty: "20",
    regularUnitPrice: "",
    dealUnitPrice: "",
  });
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const columns: Column<SupplierRoundSummary>[] = [
    { key: "sku", label: "품목", sortValue: (r) => r.sku.nameKo, render: (r) => `${r.sku.nameKo} · ${r.sku.supplier.name}` },
    { key: "status", label: "상태", render: (r) => <RoundStatusBadge status={r.status} /> },
    {
      key: "progress",
      label: "취합",
      render: (r) => (
        <div className="w-40">
          <ProgressBar percent={r.progressPercent} met={r.thresholdMet} />
          <span className="text-xs text-stone-500">
            {formatQty(r.committedQty, r.sku)} / {formatQty(r.thresholdQty, r.sku)}
            {Number(r.confirmedQty) > 0 ? ` · 확정 ${formatQty(r.confirmedQty, r.sku)}` : ""}
          </span>
        </div>
      ),
    },
    { key: "price", label: "단가", sortValue: (r) => Number(r.dealUnitPrice), render: (r) => formatMoney(r.dealUnitPrice) },
    { key: "closesAt", label: "마감", sortValue: (r) => r.closesAt, render: (r) => formatDateTime(r.closesAt) },
    {
      key: "actions",
      label: "",
      render: (r) => (
        <button
          type="button"
          className="text-sm text-brand-700 underline"
          onClick={() => setSelectedId(r.id === selectedId ? null : r.id)}
        >
          {r.id === selectedId ? "닫기" : "참여 현황"}
        </button>
      ),
    },
  ];

  const selected = rounds.data?.find((r) => r.id === selectedId);

  return (
    <AdminShell title="공동구매 관리">
      <details className="mb-6 rounded-xl border border-stone-200 bg-white p-4">
        <summary className="cursor-pointer font-semibold">새 라운드 만들기</summary>
        <form
          className="mt-4 grid gap-4 md:grid-cols-2"
          onSubmit={(e) => {
            e.preventDefault();
            createRound.mutate({
              skuId: form.skuId,
              opensAt: new Date(form.opensAt).toISOString(),
              closesAt: new Date(form.closesAt).toISOString(),
              thresholdQty: form.thresholdQty,
              regularUnitPrice: form.regularUnitPrice,
              dealUnitPrice: form.dealUnitPrice,
            });
          }}
        >
          <Field label="품목" htmlFor="skuId">
            <select
              id="skuId"
              required
              className={inputClass}
              value={form.skuId}
              onChange={(e) => setForm({ ...form, skuId: e.target.value })}
            >
              <option value="">선택하세요</option>
              {skus.data?.map((sku) => (
                <option key={sku.id} value={sku.id}>
                  {sku.supplier.name} · {sku.nameKo} ({sku.unit})
                </option>
              ))}
            </select>
          </Field>
          <Field label="시작" htmlFor="opensAt">
            <input
              id="opensAt"
              type="datetime-local"
              required
              className={inputClass}
              value={form.opensAt}
              onChange={(e) => setForm({ ...form, opensAt: e.target.value })}
            />
          </Field>
          <Field label="마감" htmlFor="closesAt">
            <input
              id="closesAt"
              type="datetime-local"
              required
              className={inputClass}
              value={form.closesAt}
              onChange={(e) => setForm({ ...form, closesAt: e.target.value })}
            />
          </Field>
          <Field label="목표 수량" htmlFor="thresholdQty">
            <input
              id="thresholdQty"
              inputMode="decimal"
              required
              className={inputClass}
              value={form.thresholdQty}
              onChange={(e) => setForm({ ...form, thresholdQty: e.target.value })}
            />
          </Field>
          <Field label="정상 단가 (예: 30.00)" htmlFor="regularUnitPrice">
            <input
              id="regularUnitPrice"
              inputMode="decimal"
              required
              className={inputClass}
              value={form.regularUnitPrice}
              onChange={(e) => setForm({ ...form, regularUnitPrice: e.target.value })}
            />
          </Field>
          <Field label="공동구매 단가" htmlFor="dealUnitPrice">
            <input
              id="dealUnitPrice"
              inputMode="decimal"
              required
              className={inputClass}
              value={form.dealUnitPrice}
              onChange={(e) => setForm({ ...form, dealUnitPrice: e.target.value })}
            />
          </Field>
          {createRound.error ? (
            <p role="alert" className="text-sm text-red-600 md:col-span-2">
              {(createRound.error as Error).message}
            </p>
          ) : null}
          <button type="submit" className={`${buttonClass} md:col-span-2`} disabled={createRound.isPending}>
            라운드 만들기
          </button>
        </form>
      </details>

      <DataTable
        columns={columns}
        rows={rounds.data ?? []}
        rowKey={(r) => r.id}
        emptyMessage="진행 중인 라운드가 없습니다."
        isPending={rounds.isPending}
      />
      {selected ? <div className="mt-4">
        <RoundDetail round={selected} />
      </div> : null}
    </AdminShell>
  );
}
